import { useEffect, useMemo, useState } from "react";
import { formatEther, parseEther, parseGwei } from "viem";
import {
  useAccount,
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";

import { factoryAbi } from "../contracts/factoryAbi";
import { factoryAddress } from "../contracts/factoryAddress";

type TradeSide = "YES" | "NO";
type TradeMode = "BUY" | "SELL";

type TradePanelProps = {
  marketId: string;
  marketTitle?: string;
  onTradeSuccess?: () => void | Promise<void>;
};

type NormalizedMarket = {
  id: bigint;
  question: string;
  resolutionSource: string;
  deadline: bigint;
  creator: string;
  resolved: boolean;
  outcome: boolean;
  yesPool: bigint;
  noPool: bigint;
  totalYesShares: bigint;
  totalNoShares: bigint;
  virtualLiquidity: bigint;
  resolutionEvidenceUrl: string;
  resolutionNotes: string;
  resolver: string;
  resolvedAt: bigint;
};

type NormalizedPosition = {
  yesShares: bigint;
  noShares: bigint;
  alreadyClaimed: boolean;
};

const AMOY_MAX_PRIORITY_FEE_PER_GAS = parseGwei("30");
const AMOY_MAX_FEE_PER_GAS = parseGwei("60");

function formatPol(value: bigint, decimals = 4) {
  try {
    const num = Number(formatEther(value));
    if (!Number.isFinite(num)) return "0";
    return num.toFixed(decimals);
  } catch {
    return "0";
  }
}

function formatSharePriceFromBps(bps: number) {
  return `${(bps / 10000).toFixed(2)} POL`;
}

function formatPercentFromBps(bps: number) {
  return `${(bps / 100).toFixed(1)}%`;
}

function formatPointsFromBpsDelta(bpsDelta: number) {
  const pts = bpsDelta / 100;
  return `${pts > 0 ? "+" : ""}${pts.toFixed(1)} pts`;
}

function safeBigInt(value: unknown): bigint {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return BigInt(value);
  if (typeof value === "string") {
    try {
      return BigInt(value);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function safeBoolean(value: unknown): boolean {
  return Boolean(value);
}

function safeString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeMarket(raw: unknown): NormalizedMarket {
  if (Array.isArray(raw)) {
    return {
      id: safeBigInt(raw[0]),
      question: safeString(raw[1]),
      resolutionSource: safeString(raw[2]),
      deadline: safeBigInt(raw[3]),
      creator: safeString(raw[4]),
      resolved: safeBoolean(raw[5]),
      outcome: safeBoolean(raw[6]),
      yesPool: safeBigInt(raw[7]),
      noPool: safeBigInt(raw[8]),
      totalYesShares: safeBigInt(raw[9]),
      totalNoShares: safeBigInt(raw[10]),
      virtualLiquidity: safeBigInt(raw[11]),
      resolutionEvidenceUrl: safeString(raw[12]),
      resolutionNotes: safeString(raw[13]),
      resolver: safeString(raw[14]),
      resolvedAt: safeBigInt(raw[15]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      id: safeBigInt(obj.id),
      question: safeString(obj.question),
      resolutionSource: safeString(obj.resolutionSource),
      deadline: safeBigInt(obj.deadline),
      creator: safeString(obj.creator),
      resolved: safeBoolean(obj.resolved),
      outcome: safeBoolean(obj.outcome),
      yesPool: safeBigInt(obj.yesPool),
      noPool: safeBigInt(obj.noPool),
      totalYesShares: safeBigInt(obj.totalYesShares),
      totalNoShares: safeBigInt(obj.totalNoShares),
      virtualLiquidity: safeBigInt(obj.virtualLiquidity),
      resolutionEvidenceUrl: safeString(obj.resolutionEvidenceUrl),
      resolutionNotes: safeString(obj.resolutionNotes),
      resolver: safeString(obj.resolver),
      resolvedAt: safeBigInt(obj.resolvedAt),
    };
  }

  return {
    id: 0n,
    question: "",
    resolutionSource: "",
    deadline: 0n,
    creator: "",
    resolved: false,
    outcome: false,
    yesPool: 0n,
    noPool: 0n,
    totalYesShares: 0n,
    totalNoShares: 0n,
    virtualLiquidity: 0n,
    resolutionEvidenceUrl: "",
    resolutionNotes: "",
    resolver: "",
    resolvedAt: 0n,
  };
}

function normalizePosition(raw: unknown): NormalizedPosition {
  if (Array.isArray(raw)) {
    return {
      yesShares: safeBigInt(raw[0]),
      noShares: safeBigInt(raw[1]),
      alreadyClaimed: safeBoolean(raw[2]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      yesShares: safeBigInt(obj.yesAmount ?? obj.yesShares),
      noShares: safeBigInt(obj.noAmount ?? obj.noShares),
      alreadyClaimed: safeBoolean(obj.alreadyClaimed),
    };
  }

  return {
    yesShares: 0n,
    noShares: 0n,
    alreadyClaimed: false,
  };
}

function isExpired(deadline: bigint) {
  if (deadline <= 0n) return false;
  const now = BigInt(Math.floor(Date.now() / 1000));
  return now >= deadline;
}

function formatCountdown(deadline: bigint) {
  if (deadline <= 0n) return "—";

  const nowSeconds = Math.floor(Date.now() / 1000);
  const remaining = Number(deadline) - nowSeconds;

  if (remaining <= 0) return "Closed";

  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function getSlippageLabel(deltaBps: number) {
  const absDelta = Math.abs(deltaBps);

  if (absDelta < 75) {
    return {
      label: "Low",
      tone: "text-emerald-600",
      box: "border-emerald-200 bg-emerald-50",
    };
  }

  if (absDelta < 250) {
    return {
      label: "Medium",
      tone: "text-amber-600",
      box: "border-amber-200 bg-amber-50",
    };
  }

  return {
    label: "High",
    tone: "text-rose-600",
    box: "border-rose-200 bg-rose-50",
  };
}

function getImpactWarning(deltaBps: number, mode: TradeMode) {
  const absDelta = Math.abs(deltaBps);

  if (absDelta < 75) {
    return mode === "BUY"
      ? "Small trade impact. Execution is close to the current displayed price."
      : "Small market movement. Your exit is close to the current displayed price.";
  }

  if (absDelta < 250) {
    return mode === "BUY"
      ? "This trade moves the market noticeably. Your average execution price differs from the displayed price."
      : "This sale moves the market noticeably. You may receive a lower exit price than the headline market price.";
  }

  return mode === "BUY"
    ? "Large market impact. This is a thin-liquidity trade and the price will move sharply if you execute."
    : "Large market impact. This market is thin and your sale will move the price sharply if you execute.";
}

export default function TradePanel({
  marketId,
  marketTitle,
  onTradeSuccess,
}: TradePanelProps) {
  const { address, isConnected } = useAccount();

  const [selectedSide, setSelectedSide] = useState<TradeSide>("YES");
  const [mode, setMode] = useState<TradeMode>("BUY");
  const [amount, setAmount] = useState("1");

  const parsedMarketId = useMemo(() => {
    try {
      return BigInt(marketId);
    } catch {
      return 0n;
    }
  }, [marketId]);

  const contractAddress = factoryAddress as `0x${string}`;

  const {
    data: marketRaw,
    isLoading: isMarketLoading,
    refetch: refetchMarket,
  } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "getMarket",
    args: [parsedMarketId],
  });

  const { data: pricesRaw, refetch: refetchPrices } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "getPrices",
    args: [parsedMarketId],
  });

  const {
    data: userPositionRaw,
    refetch: refetchUserPosition,
  } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "getUserPosition",
    args: address ? [parsedMarketId, address] : undefined,
    query: {
      enabled: !!address,
    },
  });

  const market = useMemo(() => normalizeMarket(marketRaw), [marketRaw]);
  const userPosition = useMemo(
    () => normalizePosition(userPositionRaw),
    [userPositionRaw]
  );

  const expired = useMemo(() => isExpired(market.deadline), [market.deadline]);

  const yesPriceBps = useMemo(() => {
    if (Array.isArray(pricesRaw) && pricesRaw[0] != null) {
      return Number(pricesRaw[0]);
    }
    return 5000;
  }, [pricesRaw]);

  const noPriceBps = useMemo(() => {
    if (Array.isArray(pricesRaw) && pricesRaw[1] != null) {
      return Number(pricesRaw[1]);
    }
    return 5000;
  }, [pricesRaw]);

  const selectedPriceBps = selectedSide === "YES" ? yesPriceBps : noPriceBps;
  const totalPool = market.yesPool + market.noPool;
  const selectedUserShares =
    selectedSide === "YES" ? userPosition.yesShares : userPosition.noShares;

  const buyAmountWei = useMemo(() => {
    if (mode !== "BUY") return null;
    try {
      return parseEther(amount || "0");
    } catch {
      return null;
    }
  }, [amount, mode]);

  const sellSharesWei = useMemo(() => {
    if (mode !== "SELL") return null;
    try {
      return parseEther(amount || "0");
    } catch {
      return null;
    }
  }, [amount, mode]);

  const isAmountValid =
    amount.trim() !== "" &&
    Number(amount) > 0 &&
    !Number.isNaN(Number(amount)) &&
    ((mode === "BUY" && buyAmountWei !== null) ||
      (mode === "SELL" && sellSharesWei !== null));

  const previewBuyEnabled =
    mode === "BUY" && !!buyAmountWei && buyAmountWei > 0n;
  const previewSellEnabled =
    mode === "SELL" && !!sellSharesWei && sellSharesWei > 0n;

  const { data: previewBuyRaw } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "previewBuy",
    args: previewBuyEnabled
      ? [parsedMarketId, selectedSide === "YES", buyAmountWei]
      : undefined,
    query: {
      enabled: previewBuyEnabled,
    },
  });

  const { data: previewSellRaw } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "previewSell",
    args: previewSellEnabled
      ? [parsedMarketId, selectedSide === "YES", sellSharesWei]
      : undefined,
    query: {
      enabled: previewSellEnabled,
    },
  });

  const buySharesOut = useMemo(() => {
    if (Array.isArray(previewBuyRaw) && previewBuyRaw[0] != null) {
      return safeBigInt(previewBuyRaw[0]);
    }
    return 0n;
  }, [previewBuyRaw]);

  const buyAvgPriceBps = useMemo(() => {
    if (Array.isArray(previewBuyRaw) && previewBuyRaw[1] != null) {
      return Number(previewBuyRaw[1]);
    }
    return selectedPriceBps;
  }, [previewBuyRaw, selectedPriceBps]);

  const buyNewYesPriceBps = useMemo(() => {
    if (Array.isArray(previewBuyRaw) && previewBuyRaw[2] != null) {
      return Number(previewBuyRaw[2]);
    }
    return yesPriceBps;
  }, [previewBuyRaw, yesPriceBps]);

  const buyNewNoPriceBps = useMemo(() => {
    if (Array.isArray(previewBuyRaw) && previewBuyRaw[3] != null) {
      return Number(previewBuyRaw[3]);
    }
    return noPriceBps;
  }, [previewBuyRaw, noPriceBps]);

  const estimatedPayoutIfWins = useMemo(() => {
    if (Array.isArray(previewBuyRaw) && previewBuyRaw[4] != null) {
      return safeBigInt(previewBuyRaw[4]);
    }
    return 0n;
  }, [previewBuyRaw]);

  const sellGrossAmountOut = useMemo(() => {
    if (Array.isArray(previewSellRaw) && previewSellRaw[0] != null) {
      return safeBigInt(previewSellRaw[0]);
    }
    return 0n;
  }, [previewSellRaw]);

  const sellFeeAmount = useMemo(() => {
    if (Array.isArray(previewSellRaw) && previewSellRaw[1] != null) {
      return safeBigInt(previewSellRaw[1]);
    }
    return 0n;
  }, [previewSellRaw]);

  const sellNetAmountOut = useMemo(() => {
    if (Array.isArray(previewSellRaw) && previewSellRaw[2] != null) {
      return safeBigInt(previewSellRaw[2]);
    }
    return 0n;
  }, [previewSellRaw]);

  const sellAvgPriceBps = useMemo(() => {
    if (Array.isArray(previewSellRaw) && previewSellRaw[3] != null) {
      return Number(previewSellRaw[3]);
    }
    return selectedPriceBps;
  }, [previewSellRaw, selectedPriceBps]);

  const sellNewYesPriceBps = useMemo(() => {
    if (Array.isArray(previewSellRaw) && previewSellRaw[4] != null) {
      return Number(previewSellRaw[4]);
    }
    return yesPriceBps;
  }, [previewSellRaw, yesPriceBps]);

  const sellNewNoPriceBps = useMemo(() => {
    if (Array.isArray(previewSellRaw) && previewSellRaw[5] != null) {
      return Number(previewSellRaw[5]);
    }
    return noPriceBps;
  }, [previewSellRaw, noPriceBps]);

  const estimatedProfitIfWins = useMemo(() => {
    if (!buyAmountWei) return 0n;
    return estimatedPayoutIfWins - buyAmountWei;
  }, [estimatedPayoutIfWins, buyAmountWei]);

  const previewYesAfter =
    mode === "BUY" ? buyNewYesPriceBps : sellNewYesPriceBps;
  const previewNoAfter =
    mode === "BUY" ? buyNewNoPriceBps : sellNewNoPriceBps;

  const selectedSideCurrentBps =
    selectedSide === "YES" ? yesPriceBps : noPriceBps;
  const selectedSideAfterBps =
    selectedSide === "YES" ? previewYesAfter : previewNoAfter;
  const selectedSideDeltaBps = selectedSideAfterBps - selectedSideCurrentBps;

  const avgExecutionVsHeadlineBps =
    mode === "BUY"
      ? buyAvgPriceBps - selectedSideCurrentBps
      : sellAvgPriceBps - selectedSideCurrentBps;

  const impactMeta = getSlippageLabel(selectedSideDeltaBps);
  const executionMeta = getSlippageLabel(avgExecutionVsHeadlineBps);

  const currentFee =
    mode === "BUY" && buyAmountWei
      ? buyAmountWei / 100n
      : mode === "SELL"
      ? sellFeeAmount
      : 0n;

  const {
    writeContract,
    data: txHash,
    isPending: isWalletPending,
    error: writeError,
  } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isConfirmed } =
    useWaitForTransactionReceipt({
      hash: txHash,
    });

  useEffect(() => {
    async function refreshAfterTrade() {
      if (!isConfirmed) return;
      await Promise.all([
        refetchMarket(),
        refetchPrices(),
        refetchUserPosition(),
      ]);
      await onTradeSuccess?.();
    }

    refreshAfterTrade();
  }, [
    isConfirmed,
    refetchMarket,
    refetchPrices,
    refetchUserPosition,
    onTradeSuccess,
  ]);

  const isSubmitting = isWalletPending || isConfirming;

  function handleSubmit() {
    if (!isConnected) return;
    if (market.resolved || expired) return;

    if (mode === "BUY") {
      if (!buyAmountWei || !isAmountValid) return;

      writeContract({
        address: contractAddress,
        abi: factoryAbi,
        functionName: "buyShares",
        args: [parsedMarketId, selectedSide === "YES"],
        value: buyAmountWei,
        maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
        maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
      } as any);
      return;
    }

    if (mode === "SELL") {
      if (!sellSharesWei || !isAmountValid) return;
      if (sellSharesWei > selectedUserShares) return;

      writeContract({
        address: contractAddress,
        abi: factoryAbi,
        functionName: "sellShares",
        args: [parsedMarketId, selectedSide === "YES", sellSharesWei],
        maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
        maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
      } as any);
    }
  }

  const sellAmountTooHigh =
    mode === "SELL" && !!sellSharesWei && sellSharesWei > selectedUserShares;

  const tradeDisabled =
    !isConnected ||
    !isAmountValid ||
    isSubmitting ||
    isMarketLoading ||
    market.resolved ||
    expired ||
    sellAmountTooHigh;

  const titleToShow = marketTitle || market.question || `Market #${marketId}`;

  function quickSetAmount(nextAmount: string) {
    setAmount(nextAmount);
  }

  return (
    <div className="rounded-[28px] border border-border/70 bg-card/95 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.06)] backdrop-blur">
      <div className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Trade</h2>
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {titleToShow}
            </p>
          </div>

          <div className="rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground">
            Market #{market.id.toString() || marketId}
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between rounded-2xl border border-border/70 bg-background/70 px-4 py-3">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            Market status
          </div>
          <div className="mt-1 text-sm font-medium">
            {market.resolved ? "Resolved" : expired ? "Closed" : "Open"}
          </div>
        </div>

        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            Closes in
          </div>
          <div className="mt-1 text-sm font-medium">
            {formatCountdown(market.deadline)}
          </div>
        </div>
      </div>

      <div className="mb-4 rounded-2xl bg-muted/40 p-1">
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={() => setMode("BUY")}
            className={`rounded-2xl px-4 py-3 text-left transition ${
              mode === "BUY"
                ? "bg-foreground text-background shadow-sm"
                : "bg-transparent text-foreground hover:bg-background/70"
            }`}
          >
            <div className="text-xs font-medium opacity-90">Mode</div>
            <div className="mt-1 text-lg font-semibold">Buy</div>
          </button>

          <button
            type="button"
            onClick={() => setMode("SELL")}
            className={`rounded-2xl px-4 py-3 text-left transition ${
              mode === "SELL"
                ? "bg-foreground text-background shadow-sm"
                : "bg-transparent text-foreground hover:bg-background/70"
            }`}
          >
            <div className="text-xs font-medium opacity-90">Mode</div>
            <div className="mt-1 text-lg font-semibold">Sell</div>
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-2xl bg-muted/40 p-1">
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={() => setSelectedSide("YES")}
            className={`rounded-2xl px-4 py-3 text-left transition ${
              selectedSide === "YES"
                ? "bg-emerald-500 text-white shadow-sm"
                : "bg-transparent text-foreground hover:bg-background/70"
            }`}
          >
            <div className="text-xs font-medium opacity-90">
              {mode === "BUY" ? "Buy YES" : "Sell YES"}
            </div>
            <div className="mt-1 text-lg font-semibold">
              {formatSharePriceFromBps(yesPriceBps)}
            </div>
            <div className="mt-1 text-xs opacity-80">
              {formatPercentFromBps(yesPriceBps)} chance
            </div>
          </button>

          <button
            type="button"
            onClick={() => setSelectedSide("NO")}
            className={`rounded-2xl px-4 py-3 text-left transition ${
              selectedSide === "NO"
                ? "bg-rose-500 text-white shadow-sm"
                : "bg-transparent text-foreground hover:bg-background/70"
            }`}
          >
            <div className="text-xs font-medium opacity-90">
              {mode === "BUY" ? "Buy NO" : "Sell NO"}
            </div>
            <div className="mt-1 text-lg font-semibold">
              {formatSharePriceFromBps(noPriceBps)}
            </div>
            <div className="mt-1 text-xs opacity-80">
              {formatPercentFromBps(noPriceBps)} chance
            </div>
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-[24px] border border-border/70 bg-muted/20 p-4">
        <div className="mb-3 text-sm font-medium">Your position</div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">YES shares</span>
            <span className="font-medium">{formatPol(userPosition.yesShares)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">NO shares</span>
            <span className="font-medium">{formatPol(userPosition.noShares)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              Available to {mode === "BUY" ? "buy" : "sell"} on {selectedSide}
            </span>
            <span className="font-medium">
              {mode === "BUY" ? "—" : `${formatPol(selectedUserShares)} shares`}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="trade-amount" className="mb-2 block text-sm font-medium">
          {mode === "BUY"
            ? "How much POL do you want to spend?"
            : `How many ${selectedSide} shares do you want to sell?`}
        </label>

        <div className="relative">
          <input
            id="trade-amount"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={
              mode === "BUY" ? "Enter POL amount" : "Enter shares amount"
            }
            className="h-14 w-full rounded-2xl border border-border/70 bg-background pl-4 pr-20 text-lg outline-none transition focus:ring-2"
          />
          <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
            {mode === "BUY" ? "POL" : "Shares"}
          </div>
        </div>

        {sellAmountTooHigh ? (
          <p className="mt-2 text-sm text-rose-600">
            You cannot sell more shares than you own.
          </p>
        ) : null}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {mode === "BUY" ? (
          <>
            <button
              type="button"
              onClick={() => quickSetAmount("0.1")}
              className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              0.1
            </button>
            <button
              type="button"
              onClick={() => quickSetAmount("0.5")}
              className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              0.5
            </button>
            <button
              type="button"
              onClick={() => quickSetAmount("1")}
              className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              1
            </button>
            <button
              type="button"
              onClick={() => quickSetAmount("5")}
              className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              5
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => quickSetAmount("0.25")}
              className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              0.25
            </button>
            <button
              type="button"
              onClick={() => quickSetAmount("0.5")}
              className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              0.5
            </button>
            <button
              type="button"
              onClick={() => quickSetAmount("1")}
              className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              1
            </button>
          </>
        )}
      </div>

      <div className="mb-4 rounded-[24px] border border-border/70 bg-muted/20 p-4">
        <div className="mb-3 text-sm font-medium">Execution preview</div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-border/70 bg-background/80 p-4">
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
              Current {selectedSide}
            </div>
            <div className="mt-2 text-2xl font-semibold">
              {formatPercentFromBps(selectedSideCurrentBps)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Headline market probability before your trade
            </div>
          </div>

          <div className="rounded-2xl border border-border/70 bg-background/80 p-4">
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
              After your {mode.toLowerCase()}
            </div>
            <div className="mt-2 text-2xl font-semibold">
              {formatPercentFromBps(selectedSideAfterBps)}
            </div>
            <div
              className={`mt-1 text-xs font-medium ${
                selectedSideDeltaBps > 0
                  ? "text-emerald-600"
                  : selectedSideDeltaBps < 0
                  ? "text-rose-600"
                  : "text-muted-foreground"
              }`}
            >
              Market move: {formatPointsFromBpsDelta(selectedSideDeltaBps)}
            </div>
          </div>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className={`rounded-2xl border p-4 ${impactMeta.box}`}>
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
              Price impact
            </div>
            <div className={`mt-2 text-lg font-semibold ${impactMeta.tone}`}>
              {impactMeta.label}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {Math.abs(selectedSideDeltaBps / 100).toFixed(1)} pts move in displayed probability
            </div>
          </div>

          <div className={`rounded-2xl border p-4 ${executionMeta.box}`}>
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
              Execution slippage
            </div>
            <div className={`mt-2 text-lg font-semibold ${executionMeta.tone}`}>
              {executionMeta.label}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Avg execution vs displayed price:{" "}
              {formatPointsFromBpsDelta(avgExecutionVsHeadlineBps)}
            </div>
          </div>
        </div>

        <div className="mt-3 rounded-2xl border border-border/70 bg-background/70 p-4 text-sm text-muted-foreground">
          {getImpactWarning(selectedSideDeltaBps, mode)}
        </div>
      </div>

      <div className="mb-4 rounded-[24px] border border-border/70 bg-muted/20 p-4">
        <div className="mb-3 text-sm font-medium">Trade summary</div>

        <div className="space-y-2 text-sm">
          {mode === "BUY" ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">You pay</span>
                <span className="font-medium">
                  {buyAmountWei ? formatPol(buyAmountWei) : "0"} POL
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Protocol fee</span>
                <span className="font-medium">{formatPol(currentFee)} POL</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">You receive</span>
                <span className="font-medium">
                  {formatPol(buySharesOut)} {selectedSide} shares
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Average price</span>
                <span className="font-medium">
                  {formatSharePriceFromBps(buyAvgPriceBps)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">If {selectedSide} wins</span>
                <span className="font-medium">
                  {formatPol(estimatedPayoutIfWins)} POL
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Potential profit</span>
                <span className="font-medium">
                  {formatPol(estimatedProfitIfWins)} POL
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">You sell</span>
                <span className="font-medium">
                  {sellSharesWei ? formatPol(sellSharesWei) : "0"} {selectedSide} shares
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Gross proceeds</span>
                <span className="font-medium">
                  {formatPol(sellGrossAmountOut)} POL
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Protocol fee</span>
                <span className="font-medium">{formatPol(sellFeeAmount)} POL</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">You receive</span>
                <span className="font-medium">
                  {formatPol(sellNetAmountOut)} POL
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Average sell price</span>
                <span className="font-medium">
                  {formatSharePriceFromBps(sellAvgPriceBps)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mb-4 rounded-[24px] border border-border/70 bg-muted/20 p-4">
        <div className="mb-3 text-sm font-medium">Market info</div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">YES price now</span>
            <span className="font-medium">{formatSharePriceFromBps(yesPriceBps)}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">NO price now</span>
            <span className="font-medium">{formatSharePriceFromBps(noPriceBps)}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              YES after your {mode.toLowerCase()}
            </span>
            <span className="font-medium">
              {formatSharePriceFromBps(previewYesAfter)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              NO after your {mode.toLowerCase()}
            </span>
            <span className="font-medium">
              {formatSharePriceFromBps(previewNoAfter)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Total market pool</span>
            <span className="font-medium">{formatPol(totalPool)} POL</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Virtual liquidity</span>
            <span className="font-medium">{formatPol(market.virtualLiquidity)} POL</span>
          </div>
        </div>
      </div>

      {(market.resolved || expired) && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {market.resolved
            ? "This market has already been resolved. Trading is closed."
            : "This market has already closed. Trading is no longer available."}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={tradeDisabled}
        className={`h-14 w-full rounded-2xl px-4 text-base font-semibold text-white transition ${
          selectedSide === "YES"
            ? "bg-emerald-500 hover:bg-emerald-600"
            : "bg-rose-500 hover:bg-rose-600"
        } disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {!isConnected
          ? "Connect wallet to trade"
          : market.resolved
          ? "Market resolved"
          : expired
          ? "Market closed"
          : isWalletPending
          ? "Confirm in wallet..."
          : isConfirming
          ? "Transaction pending..."
          : `${mode === "BUY" ? "Buy" : "Sell"} ${selectedSide}`}
      </button>

      <p className="mt-3 text-xs text-muted-foreground">
        {mode === "BUY"
          ? "You are buying outcome shares. If your side is correct at resolution, those shares pay out."
          : "You are selling existing shares back into the market before resolution."}
      </p>

      {writeError ? (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-600">
            Trade failed: {writeError.message}
          </p>
        </div>
      ) : null}

      {txHash ? (
        <div className="mt-4 rounded-2xl border border-border/70 bg-background/60 p-4">
          <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            Transaction
          </div>
          <p className="mt-2 break-all text-sm text-muted-foreground">{txHash}</p>
        </div>
      ) : null}

      {isConfirmed ? (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-medium text-emerald-600">
            Trade confirmed successfully.
          </p>

          <button
            type="button"
            onClick={async () => {
              await Promise.all([
                refetchMarket(),
                refetchPrices(),
                refetchUserPosition(),
              ]);
              await onTradeSuccess?.();
            }}
            className="mt-2 text-sm font-medium text-foreground underline"
          >
            Refresh market
          </button>
        </div>
      ) : null}
    </div>
  );
}